import { useState } from "react"
import { motion } from "framer-motion"
import {
  RiCheckboxCircleFill,
  RiGiftLine,
  RiMailSendLine,
  RiSparkling2Fill,
  RiSparkling2Line,
} from "@remixicon/react"
import { Link } from "react-router-dom"

import { BentoCard } from "@/components/landing/BentoCard"
import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand"
import { MarketingPageShell } from "@/components/landing/MarketingPageShell"
import { fadeIn, fadeUp } from "@/components/landing/marketing-motion"
import { Button } from "@/components/ui/button"
import { useStartCta } from "@/hooks/useAppSession"
import { APP_BILLING_URL } from "@/lib/site"
import { cn } from "@/lib/utils"

const freeFeatures = [
  "Your Sqaleshop sales link",
  "Basic products",
  "Basic orders",
  "Sqaleshop branding",
  "Basic sales analytics",
  "Email support",
] as const

const growthFeatures = [
  "More products",
  "Custom appearance & domain",
  "Advanced analytics",
  "Customer insights",
  "Abandoned order recovery",
  "WhatsApp remarketing",
  "Repeat customer tools",
  "Remove Sqaleshop branding",
  "Sqaleshop AI in analytics",
] as const

type PlanCell = boolean | string

const planComparison: { feature: string; free: PlanCell; growth: PlanCell }[] = [
  { feature: "Sales link", free: true, growth: true },
  { feature: "Products", free: "Basic", growth: "More products" },
  { feature: "Orders", free: "Basic", growth: true },
  {
    feature: "Branding",
    free: "Sqaleshop branding",
    growth: "Remove Sqaleshop branding",
  },
  {
    feature: "Analytics",
    free: "Basic (visits → orders → revenue)",
    growth: "Advanced + Sqaleshop AI",
  },
  { feature: "Custom appearance / domain", free: false, growth: true },
  { feature: "Customer insights", free: false, growth: true },
  { feature: "Abandoned order recovery", free: false, growth: true },
  { feature: "WhatsApp remarketing", free: false, growth: true },
  { feature: "Repeat customer tools", free: false, growth: true },
  { feature: "Email support", free: true, growth: true },
]

function ComparisonCell({
  value,
  accent,
}: {
  value: PlanCell
  accent?: boolean
}) {
  if (value === true) {
    return (
      <div className="flex justify-center sm:justify-start">
        <RiCheckboxCircleFill
          className={
            accent
              ? "size-5 shrink-0 text-secondary"
              : "size-5 shrink-0 text-primary/80"
          }
          aria-label="Included"
        />
      </div>
    )
  }
  if (value === false) {
    return (
      <span className="text-sm font-semibold text-primary/35" aria-label="Not included">
        —
      </span>
    )
  }
  return <span className="text-sm font-semibold text-primary">{value}</span>
}

const GROWTH_MONTHLY_NGN = 5_000
const GROWTH_YEARLY_NGN = 45_000

function formatNgn(amount: number) {
  return `₦${amount.toLocaleString("en-NG")}`
}

export function PricingPage() {
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly")
  const start = useStartCta("Claim your link")

  return (
    <MarketingPageShell
      eyebrow="Pricing"
      title="Start free. Grow when you're ready."
      description="You don't need an ecommerce website, you need your sales link. Pick what fits where you are today."
      heroVariant="primary"
      bare
    >
      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <div
            className="mb-10 flex flex-col items-center gap-3"
            role="radiogroup"
            aria-label="Billing period"
          >
            <div className="inline-flex rounded-full bg-white p-1.5 shadow-[0_12px_32px_-20px_rgba(69,24,146,0.2)]">
              <button
                type="button"
                role="radio"
                aria-checked={billing === "monthly"}
                onClick={() => setBilling("monthly")}
                className={cn(
                  "rounded-full px-6 py-2.5 text-sm font-semibold transition-colors",
                  billing === "monthly"
                    ? "bg-primary text-white"
                    : "text-primary/60 hover:text-primary",
                )}
              >
                Monthly
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={billing === "yearly"}
                onClick={() => setBilling("yearly")}
                className={cn(
                  "rounded-full px-6 py-2.5 text-sm font-semibold transition-colors",
                  billing === "yearly"
                    ? "bg-primary text-white"
                    : "text-primary/60 hover:text-primary",
                )}
              >
                Yearly
              </button>
            </div>
            {billing === "yearly" ? (
              <p className="text-center text-xs font-semibold text-primary/55">
                Growth yearly saves you{" "}
                {formatNgn(GROWTH_MONTHLY_NGN * 12 - GROWTH_YEARLY_NGN)} vs paying
                monthly for a year.
              </p>
            ) : null}
          </div>

          <div className="grid gap-5 lg:grid-cols-3 lg:items-stretch">
            <motion.div {...fadeIn}>
              <BentoCard className="h-full" hover={false}>
                <div className="inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <RiGiftLine className="size-6" />
                </div>
                <h2 className="font-heading mt-6 text-2xl text-primary">Free</h2>
                <p className="mt-2 text-sm font-semibold text-primary/60">
                  Your sales link with the essentials to start selling.
                </p>
                <div className="mt-5">
                  <span className="font-display text-4xl text-primary">₦0</span>
                  <span className="font-semibold text-primary/50"> / forever</span>
                </div>
                <ul className="mt-8 flex-1 space-y-3">
                  {freeFeatures.map((f) => (
                    <li
                      key={f}
                      className="flex gap-3 text-sm font-semibold text-primary"
                    >
                      <RiCheckboxCircleFill className="mt-0.5 size-5 shrink-0 text-primary/80" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  size="xl"
                  variant="outline"
                  className="mt-10 w-full rounded-full font-semibold"
                  asChild
                >
                  <Link to={start.href}>{start.label}</Link>
                </Button>
              </BentoCard>
            </motion.div>

            <motion.div
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.05 }}
            >
              <BentoCard
                className="relative h-full ring-2 ring-primary"
                hover={false}
                variant="default"
              >
                <div className="font-heading absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs text-white shadow-sm">
                  <RiSparkling2Line className="size-3.5" />
                  Most popular
                </div>
                <div className="mt-4 inline-flex size-12 items-center justify-center rounded-2xl bg-secondary/20 text-primary">
                  <RiSparkling2Fill className="size-6" />
                </div>
                <h2 className="font-heading mt-6 text-2xl text-primary">Growth</h2>
                <p className="mt-2 text-sm font-semibold text-primary/60">
                  Insight, remarketing, and a link that looks fully yours.
                </p>
                <div className="mt-5 flex flex-wrap items-baseline gap-1">
                  <span className="font-display text-4xl text-primary sm:text-5xl">
                    {billing === "monthly"
                      ? formatNgn(GROWTH_MONTHLY_NGN)
                      : formatNgn(GROWTH_YEARLY_NGN)}
                  </span>
                  <span className="font-semibold text-primary/50">
                    {billing === "monthly" ? "/ month" : "/ year"}
                  </span>
                </div>
                {billing === "yearly" ? (
                  <p className="mt-2 text-xs font-semibold text-primary/50">
                    ≈ {formatNgn(Math.round(GROWTH_YEARLY_NGN / 12))} / month when
                    billed annually.
                  </p>
                ) : null}
                <ul className="mt-8 flex-1 space-y-3">
                  {growthFeatures.map((f) => (
                    <li
                      key={f}
                      className="flex gap-3 text-sm font-semibold text-primary"
                    >
                      <RiCheckboxCircleFill className="mt-0.5 size-5 shrink-0 text-secondary" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  size="xl"
                  className="mt-10 w-full rounded-full font-semibold shadow-none"
                  asChild
                >
                  <a href={APP_BILLING_URL}>Subscribe in the app</a>
                </Button>
              </BentoCard>
            </motion.div>

            <motion.div
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.1 }}
            >
              <BentoCard className="h-full" hover={false} variant="lavender">
                <div className="inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <RiMailSendLine className="size-6" />
                </div>
                <h2 className="font-heading mt-6 text-2xl text-primary">Custom</h2>
                <p className="mt-3 flex-1 text-sm font-semibold leading-relaxed text-primary/65">
                  For multi-location brands, agencies, or teams that need
                  bespoke onboarding and integrations.
                </p>
                <ul className="mt-6 space-y-2 text-sm font-semibold text-primary">
                  <li className="flex gap-2">
                    <RiCheckboxCircleFill className="mt-0.5 size-5 shrink-0 text-primary/70" />
                    Dedicated success and scoped SLAs
                  </li>
                  <li className="flex gap-2">
                    <RiCheckboxCircleFill className="mt-0.5 size-5 shrink-0 text-primary/70" />
                    Custom invoicing where required
                  </li>
                  <li className="flex gap-2">
                    <RiCheckboxCircleFill className="mt-0.5 size-5 shrink-0 text-primary/70" />
                    Roadmap input for eligible partners
                  </li>
                </ul>
                <Button
                  size="xl"
                  className="mt-10 w-full rounded-full bg-primary font-semibold text-white shadow-none hover:bg-primary/90"
                  asChild
                >
                  <Link to="/contact">Talk to sales</Link>
                </Button>
              </BentoCard>
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      <ColorBand className="bg-white text-primary">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            id="pricing-compare-heading"
            className="font-display text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Free vs Growth
          </motion.h2>
          <motion.p
            {...fadeUp}
            className="mt-4 max-w-2xl text-base font-semibold text-primary/65"
          >
            Side-by-side view of what each plan includes.
          </motion.p>

          <motion.div
            {...fadeIn}
            className="mt-10 overflow-x-auto rounded-[2rem] bg-[#f6f4ef] shadow-[0_16px_40px_-28px_rgba(69,24,146,0.18)]"
          >
            <table className="w-full min-w-[520px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-primary/10">
                  <th
                    scope="col"
                    className="px-5 py-4 font-heading font-semibold text-primary sm:px-7"
                  >
                    Feature
                  </th>
                  <th
                    scope="col"
                    className="px-5 py-4 font-heading font-semibold text-primary sm:px-7"
                  >
                    Free
                  </th>
                  <th
                    scope="col"
                    className="bg-primary/5 px-5 py-4 font-heading font-semibold text-primary sm:px-7"
                  >
                    Growth
                  </th>
                </tr>
              </thead>
              <tbody>
                {planComparison.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={i % 2 === 1 ? "bg-white/50" : ""}
                  >
                    <th
                      scope="row"
                      className="border-t border-primary/8 px-5 py-4 font-semibold text-primary sm:px-7"
                    >
                      {row.feature}
                    </th>
                    <td className="border-t border-primary/8 px-5 py-4 sm:px-7">
                      <ComparisonCell value={row.free} />
                    </td>
                    <td className="border-t border-primary/8 bg-primary/5 px-5 py-4 sm:px-7">
                      <ComparisonCell value={row.growth} accent />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </ColorBandInner>
      </ColorBand>
    </MarketingPageShell>
  )
}
